import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import { RootState } from 'App'
import { BusinessUserType } from '__generated__/api-types-and-hooks'

interface IAttendeeCalendarData {
  organizer: IAssigneeOptions
  allAttendees: IAssigneeOptions[]
  internalAttendees: IAssigneeOptions[]
  attendeeColors: Record<string, string>
  getAttendeeEvents: Record<
    string,
    Array<{
      start: number
      end: number
      startMinute: number
      endMinute: number
      title?: string
      date: string
    }>
  >
}

interface IUseAttendeeCalendarProps {
  selectedGuests: IAssigneeOptions[]
  userAvailabilityMap: Record<string, any>
}

export const useAttendeeCalendar = ({
  selectedGuests,
  userAvailabilityMap,
}: IUseAttendeeCalendarProps): IAttendeeCalendarData => {
  // Get current user info
  const currentUser = useSelector((state: RootState) => state.user.user)

  // Create organizer object
  const organizer: IAssigneeOptions = useMemo(() => {
    // Build label with fallbacks
    const firstName = currentUser.firstName || ''
    const lastName = currentUser.lastName || ''
    let label = `${firstName} ${lastName}`.trim()

    // Fallback to name or email if firstName/lastName are empty
    if (!label) {
      label = currentUser.name || currentUser.email || 'Organizer'
    }

    return {
      label,
      value: currentUser.id,
      type: BusinessUserType.Owner,
      user: {
        id: currentUser.id,
        firstName: firstName,
        lastName: lastName,
      },
    }
  }, [currentUser])

  // Get all attendees (organizer + selected guests)
  const allAttendees = useMemo(() => {
    return [organizer, ...selectedGuests]
  }, [organizer, selectedGuests])

  // Memoize internal attendees (excluding external emails)
  const internalAttendees = useMemo(() => {
    return allAttendees.filter((a) => a.value && !a.value.includes('@'))
  }, [allAttendees])

  // Assign colors to attendees for calendar view
  const attendeeColors = useMemo(() => {
    const colors = [
      '#9333EA',
      '#06B6D4',
      '#10B981',
      '#F59E0B',
      '#EF4444',
      '#8B5CF6',
      '#EC4899',
      '#14B8A6',
    ]
    const colorMap: Record<string, string> = {}

    allAttendees.forEach((attendee, index) => {
      colorMap[attendee.value] = colors[index % colors.length]
    })

    return colorMap
  }, [allAttendees])

  // Parse calendar data for attendees (mix of calendar events and availability)
  const getAttendeeEvents = useMemo(() => {
    const events: Record<
      string,
      Array<{
        start: number
        end: number
        startMinute: number
        endMinute: number
        title?: string
        date: string
      }>
    > = {}

    // Initialize empty arrays for all attendees
    allAttendees.forEach((attendee) => {
      events[attendee.value] = []
    })

    // Parse data for each user from the map
    allAttendees.forEach((attendee) => {
      const userData = userAvailabilityMap[attendee.value]

      if (!userData) {
        return
      }

      if (userData.type === 'events') {
        // Process calendar events (real event data)
        const calendarEvents = userData.data

        if (Array.isArray(calendarEvents) && calendarEvents.length > 0) {
          calendarEvents.forEach((event: any) => {
            const startDateTime = event.start?.dateTime
            const endDateTime = event.end?.dateTime

            if (startDateTime && endDateTime) {
              const startMoment = moment(startDateTime)
              const endMoment = moment(endDateTime)

              const startHour = startMoment.hour()
              const startMinute = startMoment.minute()
              const endHour = endMoment.hour()
              const endMinute = endMoment.minute()
              const dateObj = startMoment.toDate()
              const dateString = dateObj.toDateString()

              // Store event as single block with actual start/end times
              events[attendee.value].push({
                start: startHour,
                end: endHour,
                startMinute: startMinute,
                endMinute: endMinute,
                title: event.summary || 'Busy',
                date: dateString,
              })
            }
          })
        }
      } else if (userData.type === 'availability') {
        // Process availability data (just busy/free status)
        const availabilityData = userData.data

        if (availabilityData?.availability) {
          availabilityData.availability.forEach((dayAvailability: any) => {
            const slots = dayAvailability.slots || []

            slots.forEach((slot: any) => {
              // If slot is NOT available, user is busy
              if (!slot.isAvailable && slot.slotStartTime && slot.slotEndTime) {
                const startMoment = moment(slot.slotStartTime)
                const endMoment = moment(slot.slotEndTime)

                const startHour = startMoment.hour()
                const startMinute = startMoment.minute()
                const endHour = endMoment.hour()
                const endMinute = endMoment.minute()
                const dateObj = startMoment.toDate()
                const dateString = dateObj.toDateString()

                // Store availability slot as single block
                events[attendee.value].push({
                  start: startHour,
                  end: endHour,
                  startMinute: startMinute,
                  endMinute: endMinute,
                  title: 'Busy',
                  date: dateString,
                })
              }
            })
          })
        }
      }
    })

    return events
  }, [allAttendees, userAvailabilityMap])

  return {
    organizer,
    allAttendees,
    internalAttendees,
    attendeeColors,
    getAttendeeEvents,
  }
}
